import { CalendarDays, ChevronLeft, Clapperboard, ListChecks, MapPin, PanelLeftClose, PanelLeftOpen, Settings2, Sparkles, Wallet, WifiOff } from "lucide-react";
import { lazy, Suspense, useEffect, useRef, useState, type ReactNode } from "react";
import { useAuth } from "@/lib/auth";
import { countdown, fmtRange } from "@/lib/format";
import { useUI, type PanelTab } from "@/lib/store";
import { tripApi, useDerived, useTripBundle, useTripEvents, type Bundle, type Derived } from "@/lib/trip";
import { MapSearch } from "./map/MapSearch";
import { TripMap } from "./map/TripMap";
import { ChecklistPanel } from "./panels/ChecklistPanel";
import { AddPlaceDialog } from "./places/AddPlaceDialog";
import type { PlaceDraft } from "@/lib/store";
import { PlaceDetails } from "./places/PlaceDetails";
import { PlacesPanel } from "./places/PlacesPanel";
import { PlanPanel } from "./plan/PlanPanel";
import { Timeline } from "./Timeline";
import { Avatar, Button, cx, IconButton, Spinner } from "./ui";

// тяжёлые части (markdown, виджеты, бюджет) — отдельными чанками
const ChatPanel = lazy(() => import("./chat/ChatPanel").then((m) => ({ default: m.ChatPanel })));
const SourcesPanel = lazy(() => import("./sources/SourcesPanel").then((m) => ({ default: m.SourcesPanel })));
const SourceViewer = lazy(() => import("./sources/SourcesPanel").then((m) => ({ default: m.SourceViewer })));
const BudgetPanel = lazy(() => import("./panels/BudgetPanel").then((m) => ({ default: m.BudgetPanel })));
const TripPanel = lazy(() => import("./panels/TripPanel").then((m) => ({ default: m.TripPanel })));
const WidgetViewer = lazy(() => import("./widgets/WidgetFrame").then((m) => ({ default: m.WidgetViewer })));
const Loading = () => (
  <div className="grid place-items-center py-10">
    <Spinner className="text-[var(--accent)]" />
  </div>
);

const TABS: { id: PanelTab; label: string; short: string; icon: ReactNode }[] = [
  { id: "plan", label: "План", short: "План", icon: <CalendarDays size={17} /> },
  { id: "places", label: "Места", short: "Места", icon: <MapPin size={17} /> },
  { id: "sources", label: "Материалы", short: "Медиа", icon: <Clapperboard size={17} /> },
  { id: "checklist", label: "Задачи", short: "Задачи", icon: <ListChecks size={17} /> },
  { id: "budget", label: "Бюджет", short: "Деньги", icon: <Wallet size={17} /> },
  { id: "trip", label: "Поездка", short: "Ещё", icon: <Settings2 size={17} /> },
];

function useIsDesktop() {
  const [v, setV] = useState(() => window.matchMedia("(min-width: 1024px)").matches);
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 1024px)");
    const h = () => setV(mq.matches);
    mq.addEventListener("change", h);
    return () => mq.removeEventListener("change", h);
  }, []);
  return v;
}

function useOnline() {
  const [on, setOn] = useState(navigator.onLine);
  useEffect(() => {
    const a = () => setOn(true);
    const b = () => setOn(false);
    window.addEventListener("online", a);
    window.addEventListener("offline", b);
    return () => {
      window.removeEventListener("online", a);
      window.removeEventListener("offline", b);
    };
  }, []);
  return on;
}

function PanelBody({ b, d, onOpenSource }: { b: Bundle; d: Derived; onOpenSource: (id: string, t?: number | null) => void }) {
  const tab = useUI((s) => s.tab);
  const selectedPlaceId = useUI((s) => s.selectedPlaceId);
  const place = selectedPlaceId ? d.places.get(selectedPlaceId) : null;
  if (place) return <PlaceDetails b={b} d={d} place={place} onOpenSource={onOpenSource} />;
  return <Suspense fallback={<Loading />}>{panelFor(tab, b, d)}</Suspense>;
}

function panelFor(tab: PanelTab, b: Bundle, d: Derived) {
  switch (tab) {
    case "plan":
      return <PlanPanel b={b} d={d} />;
    case "places":
      return <PlacesPanel b={b} d={d} />;
    case "sources":
      return <SourcesPanel b={b} />;
    case "checklist":
      return <ChecklistPanel b={b} d={d} />;
    case "budget":
      return <BudgetPanel b={b} d={d} />;
    case "trip":
      return <TripPanel b={b} />;
  }
}

/** Все 6 вкладок всегда помещаются в ширину: иконка над подписью, кнопки делят строку поровну */
function TabBar({ compact }: { compact?: boolean }) {
  const tab = useUI((s) => s.tab);
  const set = useUI((s) => s.set);
  const selectedPlaceId = useUI((s) => s.selectedPlaceId);
  return (
    <div role="tablist" className={cx("grid grid-cols-6 gap-0.5", compact ? "px-2" : "px-2")}>
      {TABS.map((t) => {
        const active = tab === t.id && !selectedPlaceId;
        return (
          <button
            key={t.id}
            role="tab"
            aria-selected={active}
            title={t.label}
            onClick={() => set({ tab: t.id, selectedPlaceId: null, ...(compact ? { sheet: useUI.getState().sheet === "peek" ? "half" : useUI.getState().sheet } : {}) })}
            className={cx(
              "flex min-w-0 flex-col items-center gap-0.5 rounded-xl px-0.5 py-1.5 font-bold transition",
              compact ? "text-[10px]" : "text-[11px]",
              active ? "bg-[var(--accent-soft)] text-[var(--accent)]" : "text-muted hover:bg-[var(--surface-2)] hover:text-[var(--fg)]",
            )}
          >
            {t.icon}
            <span className="w-full truncate text-center">{compact ? t.short : t.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function Presence({ b }: { b: Bundle }) {
  const presence = useUI((s) => s.presence);
  const { user } = useAuth();
  const online = b.members.filter((m) => presence.includes(m.id) && m.id !== user?.id);
  return (
    <div className="flex -space-x-2">
      {b.members.slice(0, 5).map((m) => (
        <span key={m.id} className={cx("relative", !presence.includes(m.id) && "opacity-60")}>
          <Avatar m={m} size={30} ring />
          {online.some((x) => x.id === m.id) && <span className="absolute right-0 bottom-0 size-2.5 rounded-full border-2 border-[var(--surface-solid)] bg-emerald-500" />}
        </span>
      ))}
    </div>
  );
}

export function TripView({ tripId, onBack }: { tripId: string; onBack: () => void }) {
  const q = useTripBundle(tripId);
  const { user } = useAuth();
  useTripEvents(tripId, user?.id);
  const d = useDerived(q.data);
  const desktop = useIsDesktop();
  const online = useOnline();
  const ui = useUI();
  const [source, setSource] = useState<{ id: string; t?: number | null } | null>(null);

  useEffect(() => {
    if (q.data) document.title = `${q.data.trip.title} — Tripboard`;
  }, [q.data]);

  if (q.isLoading && !q.data)
    return (
      <div className="grid h-dvh place-items-center">
        <Spinner size={28} className="text-[var(--accent)]" />
      </div>
    );
  if (!q.data || !d)
    return (
      <div className="grid h-dvh place-items-center p-6 text-center">
        <div>
          <div className="mb-2 text-4xl">🧭</div>
          <div className="mb-1 font-bold">Поездка не открылась</div>
          <div className="mb-4 text-sm text-muted">{(q.error as Error)?.message ?? "Нет доступа или нет сети"}</div>
          <Button onClick={onBack}>К списку поездок</Button>
        </div>
      </div>
    );

  const b = q.data;
  const cd = countdown(b.trip.startDate, b.trip.endDate);
  const onPick = (ll: [number, number]) => {
    if (b.trip.myRole === "viewer") return;
    ui.set({ placeDraft: { lat: ll[0], lng: ll[1], reverse: true } });
  };
  const onAddCandidate = async (sourceId: string, idx: number) => {
    if (b.trip.myRole === "viewer") return;
    await tripApi.addExtracted(b.trip.id, sourceId, [idx]);
    ui.toast("Место добавлено на карту");
  };
  const openSource = (id: string, t?: number | null) => setSource({ id, t });

  const map = (
    <TripMap b={b} d={d} onPick={onPick} onAddCandidate={onAddCandidate} controlsOffset={desktop ? 60 : 64} />
  );

  const dialogs = (
    <>
      <AddPlaceDialog b={b} d={d} />
      <Suspense fallback={null}>
        {ui.widgetOpenId && <WidgetViewer b={b} />}
        {source && <SourceViewer b={b} sourceId={source.id} t={source.t} onClose={() => setSource(null)} />}
      </Suspense>
    </>
  );

  const offlineBanner = !online && (
    <div className="glass flex items-center gap-2 rounded-full px-3 py-1.5 text-[12px] font-semibold">
      <WifiOff size={14} /> Офлайн — показана сохранённая копия
    </div>
  );

  if (desktop) {
    return (
      <div className="flex h-dvh flex-col overflow-hidden">
        <header className="z-20 flex h-14 shrink-0 items-center gap-3 border-b border-[var(--border)] bg-[var(--surface-solid)] px-3">
          <IconButton label="Все поездки" onClick={onBack}>
            <ChevronLeft size={19} />
          </IconButton>
          <div className="min-w-0">
            <div className="font-display truncate text-[16px] leading-tight font-bold">{b.trip.title}</div>
            <div className="truncate text-[12px] text-muted">
              {fmtRange(b.trip.startDate, b.trip.endDate)} · {cd.text}
            </div>
          </div>
          <IconButton label={ui.panelOpen ? "Скрыть панель" : "Показать панель"} onClick={() => ui.set({ panelOpen: !ui.panelOpen })}>
            {ui.panelOpen ? <PanelLeftClose size={18} /> : <PanelLeftOpen size={18} />}
          </IconButton>
          <div className="flex-1" />
          {offlineBanner}
          <Presence b={b} />
          <Button variant={ui.chatOpen ? "soft" : "primary"} icon={<Sparkles size={16} />} onClick={() => ui.set({ chatOpen: !ui.chatOpen })}>
            ИИ-ассистент
          </Button>
        </header>
        <div className="flex min-h-0 flex-1">
          {ui.panelOpen && (
            <aside className="flex w-[410px] shrink-0 flex-col border-r border-[var(--border)] bg-[var(--surface-solid)]">
              <div className="border-b border-[var(--border)] py-2">
                <TabBar />
              </div>
              <div key={`${ui.tab}-${ui.selectedPlaceId ?? ""}`} className="scroll-thin min-h-0 flex-1 overflow-y-auto px-3 pt-3">
                <PanelBody b={b} d={d} onOpenSource={openSource} />
              </div>
            </aside>
          )}
          <main className="relative min-w-0 flex-1">
            {map}
            <div className="pointer-events-none absolute inset-x-3 top-3 z-[600] flex justify-center">
              <MapSearch b={b} onPickExternal={(p) => ui.set({ placeDraft: p })} className="pointer-events-auto w-full max-w-md" />
            </div>
            <div className="absolute inset-x-3 bottom-3 z-[600]">
              <Timeline d={d} />
            </div>
          </main>
          {ui.chatOpen && (
            <aside className="flex w-[430px] shrink-0 flex-col border-l border-[var(--border)] bg-[var(--surface-solid)]">
              <Suspense fallback={<Loading />}>
                <ChatPanel b={b} d={d} onClose={() => ui.set({ chatOpen: false })} />
              </Suspense>
            </aside>
          )}
        </div>
        {dialogs}
      </div>
    );
  }

  return (
    <MobileLayout b={b} d={d} onBack={onBack} map={map} offline={offlineBanner} onPickExternal={(p) => ui.set({ placeDraft: p })} onOpenSource={openSource}>
      {dialogs}
    </MobileLayout>
  );
}

const SHEET_PEEK = 118;

function MobileLayout({
  b,
  d,
  onBack,
  map,
  offline,
  onPickExternal,
  onOpenSource,
  children,
}: {
  b: Bundle;
  d: Derived;
  onBack: () => void;
  map: ReactNode;
  offline: ReactNode;
  onPickExternal: (d: PlaceDraft) => void;
  onOpenSource: (id: string, t?: number | null) => void;
  children: ReactNode;
}) {
  const sheet = useUI((s) => s.sheet);
  const mobileView = useUI((s) => s.mobileView);
  const selectedPlaceId = useUI((s) => s.selectedPlaceId);
  const tab = useUI((s) => s.tab);
  const set = useUI((s) => s.set);
  const [drag, setDrag] = useState<number | null>(null);
  const start = useRef<{ y: number; h: number } | null>(null);
  const vh = typeof window !== "undefined" ? window.innerHeight : 800;
  const heights = { peek: SHEET_PEEK, half: Math.round(vh * 0.52), full: vh - 70 };
  const h = drag ?? heights[sheet];

  useEffect(() => {
    if (selectedPlaceId && sheet === "peek") set({ sheet: "half" });
  }, [selectedPlaceId, sheet, set]);

  const onDown = (e: React.PointerEvent) => {
    start.current = { y: e.clientY, h };
    (e.target as HTMLElement).setPointerCapture(e.pointerId);
  };
  const onMove = (e: React.PointerEvent) => {
    if (!start.current) return;
    setDrag(Math.max(SHEET_PEEK, Math.min(heights.full, start.current.h + (start.current.y - e.clientY))));
  };
  const onUp = () => {
    if (!start.current) return;
    const cur = drag ?? h;
    start.current = null;
    setDrag(null);
    const nearest = (Object.entries(heights) as [typeof sheet, number][]).reduce((a, z) => (Math.abs(z[1] - cur) < Math.abs(a[1] - cur) ? z : a));
    set({ sheet: nearest[0] });
  };

  return (
    <div className="relative h-dvh overflow-hidden">
      <div className="absolute inset-0">{map}</div>

      <div className="safe-top pointer-events-none absolute inset-x-0 top-0 z-[700] px-3 pt-3">
        <div className="pointer-events-auto flex items-center gap-2">
          <IconButton label="Все поездки" onClick={onBack} className="glass size-11 rounded-2xl">
            <ChevronLeft size={19} />
          </IconButton>
          <MapSearch b={b} onPickExternal={onPickExternal} className="min-w-0 flex-1" />
        </div>
        {offline && <div className="pointer-events-auto mt-2 flex justify-center">{offline}</div>}
      </div>

      <div className="pointer-events-none absolute inset-x-0 z-[650] px-2 transition-[bottom] duration-200" style={{ bottom: h + 8 }}>
        <div className="pointer-events-auto flex items-end gap-2">
          <div className="min-w-0 flex-1">{sheet !== "full" && <Timeline d={d} compact />}</div>
        </div>
      </div>

      <button
        onClick={() => set({ mobileView: "chat" })}
        className="absolute right-3 z-[660] grid size-14 place-items-center rounded-2xl bg-gradient-to-br from-jade-500 to-jade-700 text-white shadow-[var(--shadow-lg)] transition-[bottom] duration-200"
        style={{ bottom: h + (sheet !== "full" ? 92 : 12) }}
        aria-label="ИИ-ассистент"
      >
        <Sparkles size={22} />
      </button>

      <section
        className={cx("absolute inset-x-0 bottom-0 z-[700] flex flex-col rounded-t-3xl border-t border-[var(--border)] bg-[var(--surface-solid)] shadow-[var(--shadow-lg)]", drag == null && "transition-[height] duration-200")}
        style={{ height: h }}
      >
        <div className="flex cursor-grab touch-none justify-center pt-2 pb-1" onPointerDown={onDown} onPointerMove={onMove} onPointerUp={onUp} onPointerCancel={onUp}>
          <span className="h-1.5 w-10 rounded-full bg-[var(--border-strong)]" />
        </div>
        <div className="px-4 pb-1" onClick={() => sheet === "peek" && set({ sheet: "half" })}>
          <div className="flex items-baseline gap-2">
            <div className="font-display truncate text-[15px] font-bold">{b.trip.title}</div>
            <div className="truncate text-[12px] text-muted">{countdown(b.trip.startDate, b.trip.endDate).text}</div>
          </div>
        </div>
        <TabBar compact />
        <div key={`${tab}-${selectedPlaceId ?? ""}`} className="scroll-thin min-h-0 flex-1 overflow-y-auto px-3 pt-2">{sheet !== "peek" && <PanelBody b={b} d={d} onOpenSource={onOpenSource} />}</div>
      </section>

      {mobileView === "chat" && (
        <div className="animate-in fixed inset-0 z-[1800] flex flex-col bg-[var(--surface-solid)]">
          <div className="safe-top" />
          <Suspense fallback={<Loading />}>
            <ChatPanel b={b} d={d} onClose={() => set({ mobileView: "map" })} />
          </Suspense>
        </div>
      )}
      {children}
    </div>
  );
}
